import React, { useEffect, useState } from "react";
import { Box, Stack, CircularProgress } from "@mui/material";
import Modal, { ModalHead, ModalBody, ModalFoot } from "../Modal";
import BtnPrimary from "../PrimaryButton";
import BtnSecondary from "../SecondaryButton";
import InlineMessage from "../InlineMessage";
import { useNotify } from "../NotificationProvider";
import { previewLetter, issueLetter, emailLetter } from "../../services/letter";
import { errorMessageFrom } from "../../utils/formErrors";
import { tokens } from "../../theme";

const TITLE = {
  offer: "Offer letter",
  relieving: "Relieving letter",
  settlement: "Full and final settlement",
};

const SUBTITLE = {
  offer: "States the role and the pay. Not the contract itself — the terms follow on joining.",
  relieving: "Confirms dates and role, and nothing else. The next employer reads it to verify service.",
  settlement: "The final figures, taken from the full-and-final payroll run.",
};

// `**bold**` is the only markup a template emits, so the preview renders the
// same emphasis the PDF does rather than showing the asterisks.
const Rich = ({ text }) => (
  <>
    {String(text)
      .split(/(\*\*[^*]+\*\*)/)
      .filter(Boolean)
      .map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <b key={i}>{part.slice(2, -2)}</b>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
  </>
);

/**
 * Preview a letter, then download it or send it.
 *
 * It shows the words before it commits to them. These are documents somebody
 * will hold on to — an offer letter is a contract and a relieving letter is
 * evidence — so issuing one blind, from a button that just produces a file,
 * is the wrong shape.
 *
 * What is MISSING is shown as prominently as what is there. A letter renders
 * without a registration number or a signatory; it is just worth less to
 * whoever receives it, and that is better said here than discovered by the
 * person it was sent to.
 */
const LetterDialog = ({ employee, type, open, onClose, onIssued }) => {
  const notify = useNotify();
  const [letter, setLetter] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !employee?._id || !type) return undefined;
    let active = true;
    setLoading(true);
    setFailed(false);
    previewLetter(employee._id, type)
      .then((res) => {
        if (!active) return;
        if (res?.success === false) {
          setFailed(true);
          notify.error(errorMessageFrom(res, "Could not build the letter"));
          return;
        }
        setLetter(res?.data?.letter || null);
        setGaps(res?.data?.gaps || []);
      })
      .catch(() => active && setFailed(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [open, employee?._id, type, notify]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await issueLetter(employee._id, type);
      if (!res?.success || !res.blob) {
        notify.error(res?.message || "Could not produce the letter");
        return;
      }
      // The server names the file; this only puts it somewhere the browser
      // will save from. Revoked straight after, or the blob is held for the
      // life of the page.
      const url = URL.createObjectURL(res.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename || `${TITLE[type]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      notify.success(`${TITLE[type]} downloaded — it is recorded against ${employee.fname || "this employee"}`);
      onIssued?.();
      onClose?.();
    } catch (e) {
      notify.error(e?.message || "Could not produce the letter");
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    try {
      const res = await emailLetter(employee._id, type);
      if (res?.success === false) {
        notify.error(errorMessageFrom(res, "Could not send the letter"));
        return;
      }
      notify.success(`${TITLE[type]} sent to ${res?.data?.emailedTo || employee.email}`);
      onIssued?.();
      onClose?.();
    } catch (e) {
      notify.error(e?.message || "Could not send the letter");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} width="42rem">
      <ModalHead
        title={TITLE[type] || "Letter"}
        subtitle={SUBTITLE[type]}
        onClose={onClose}
      />
      <ModalBody>
        {loading ? (
          <CircularProgress size={20} />
        ) : failed || !letter ? (
          <InlineMessage tone="danger">
            This letter could not be built. Check the employee's record and try again.
          </InlineMessage>
        ) : (
          <>
            {gaps.length > 0 && (
              <Box sx={{ mb: "1.25rem" }}>
                <InlineMessage tone="warning">
                  <Box sx={{ fontWeight: 600, mb: "0.25rem" }}>
                    This will send, but it is missing:
                  </Box>
                  <Box component="ul" sx={{ m: 0, pl: "1.1rem" }}>
                    {gaps.map((g) => <li key={g}>{g}</li>)}
                  </Box>
                </InlineMessage>
              </Box>
            )}

            {/* The letter as it will read. Set on paper rather than on the
                page's own ground, because that is what it is. */}
            <Box
              sx={{
                background: "#fff",
                border: `1px solid ${tokens.border}`,
                borderRadius: "8px",
                p: "1.75rem",
                fontSize: "0.875rem",
                lineHeight: 1.75,
                color: tokens.text,
              }}
            >
              <Box sx={{ fontWeight: 700, fontSize: "1rem", mb: "0.25rem" }}>{letter.title}</Box>
              {letter.subject && (
                <Box sx={{ color: tokens.textMuted, mb: "1.25rem" }}>{letter.subject}</Box>
              )}
              {(letter.body || []).map((para, i) => (
                <Box key={i} sx={{ mb: "0.875rem" }}><Rich text={para} /></Box>
              ))}

              {letter.table?.rows?.length > 0 && (
                <Box sx={{ my: "1.25rem" }}>
                  {letter.table.rows.map((r, i) => (
                    <Stack key={i} direction="row" justifyContent="space-between" sx={{ py: "0.2rem" }}>
                      <Box>{r.label}</Box>
                      <Box>{r.value}</Box>
                    </Stack>
                  ))}
                  {letter.table.total && (
                    <Stack
                      direction="row" justifyContent="space-between"
                      sx={{ pt: "0.5rem", mt: "0.4rem", borderTop: `1px solid ${tokens.border}`, fontWeight: 700 }}
                    >
                      <Box>{letter.table.total.label}</Box>
                      <Box>{letter.table.total.value}</Box>
                    </Stack>
                  )}
                </Box>
              )}

              {(letter.after || []).map((para, i) => (
                <Box key={i} sx={{ mb: "0.875rem" }}><Rich text={para} /></Box>
              ))}
              <Box sx={{ mt: "1.5rem", color: tokens.textMuted }}>{letter.closing}</Box>
            </Box>
          </>
        )}
      </ModalBody>
      <ModalFoot note="Recorded against the employee, with who issued it and when.">
        <BtnSecondary onClick={onClose} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
        <BtnSecondary onClick={download} disabled={busy || loading || failed} sx={{ borderRadius: "8px" }}>
          Download
        </BtnSecondary>
        <BtnPrimary
          onClick={send}
          disabled={busy || loading || failed || !employee?.email}
          sx={{ borderRadius: "8px" }}
        >
          {busy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Email to employee"}
        </BtnPrimary>
      </ModalFoot>
    </Modal>
  );
};

export default LetterDialog;
